import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { FileBadge, Landmark, ShieldCheck, Eye, Zap, UsersRound } from 'lucide-react';
import { SEO, buildBreadcrumbLd } from '../components/SEO';

const BREADCRUMB = buildBreadcrumbLd([
  { name: 'Əsas səhifə', path: '/' },
  { name: 'Haqqımızda', path: '/haqqimizda/biz-kimik' },
  { name: 'Biz kimik', path: '/haqqimizda/biz-kimik' },
]);

export const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="İDK BOKT haqqında | İnkişafa Dəstək Kreditləri"
        description="İDK BOKT — 2011-ci ildən Azərbaycanda fəaliyyət göstərən qeyri-bank kredit təşkilatı. Missiyamız, dəyərlərimiz və fəaliyyət prinsiplərimiz haqqında məlumat."
        path="/haqqimizda/biz-kimik"
        jsonLd={BREADCRUMB}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white pt-12 pb-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 font-serif-display">Biz kimik?</h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              İnkişafa Dəstək Kreditləri BOKT fiziki və hüquqi şəxslərə maliyyə dəstəyi və kredit həlləri təqdim edən kredit təşkilatıdır.
            </p>
          </div>
        </div>
      </section>

      {/* Şirkət tarixçəsi və lisenziya - YENİ BÖLMƏ */}
      <section className="pt-4 pb-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-12">
            <div className="lg:col-span-3">
              <h2 className="text-3xl font-bold text-gray-900 mb-5">Şirkət haqqında</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  &quot;İnkişafa Dəstək Kreditləri&quot; Bank Olmayan Kredit Təşkilatı Məhdud Məsuliyyətli Cəmiyyəti
                  <strong className="text-gray-900"> 2011-ci ildə</strong> təsis edilmişdir.
                  25 may 2011-ci il tarixində <strong className="text-gray-900">BKT-6</strong> sayla lisenziyaların mərkəzi reyestrinə daxil edilmişdir.
                </p>
                <p>
                  Verilmiş lisenziya əsasında, fiziki və hüquqi şəxslərə qısa və uzunmüddətli, təminatlı və ya təminatsız kreditlərin verilməsi,
                  &quot;Bank olmayan kredit təşkilatları haqqında&quot; Azərbaycan Respublikası Qanununda nəzərdə tutulmuş digər fəaliyyət növləri ilə qanunvericiliyə müvafiq qaydada məşğul oluruq.
                </p>
                <p>
                  Həmçinin, Azərbaycan Respublikası Mərkəzi Bankının <strong className="text-gray-900">06/06-147 №-li</strong> xüsusi lisenziyası əsasında girov əmanəti qəbul etmək hüququna malikik.
                </p>
              </div>
            </div>

            <div className="lg:col-span-2 grid grid-cols-2 gap-4 content-start">
              <Card className="border border-blue-100">
                <CardHeader className="pb-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 mb-2">
                    <Landmark className="h-5 w-5 text-blue-700" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-blue-700">2011</CardTitle>
                  <CardDescription>Təsis tarixi</CardDescription>
                </CardHeader>
              </Card>
              <Card className="border border-blue-100">
                <CardHeader className="pb-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 mb-2">
                    <FileBadge className="h-5 w-5 text-blue-700" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-blue-700">BKT-6</CardTitle>
                  <CardDescription>Lisenziya nömrəsi</CardDescription>
                </CardHeader>
              </Card>
              <Card className="border border-blue-100 col-span-2">
                <CardHeader className="pb-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 mb-2">
                    <ShieldCheck className="h-5 w-5 text-blue-700" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-blue-700">06/06-147</CardTitle>
                  <CardDescription>Mərkəzi Bankın girov əmanəti üçün xüsusi lisenziyası</CardDescription>
                </CardHeader>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Missiyamız və dəyərlərimiz */}
      <section className="py-14 sm:py-16 bg-white border-y border-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Sol sütun - başlıq */}
            <div className="lg:col-span-5">
              <div>
                <span className="inline-block text-xs font-semibold text-blue-700 uppercase tracking-[0.2em] mb-4">
                  Missiyamız və dəyərlərimiz
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-5 leading-tight font-serif-display">
                  Hər müştəri üçün etibarlı maliyyə tərəfdaşı olmaq.
                </h2>
                <p className="text-base text-gray-600 leading-relaxed">
                  Azərbaycanda yaşayan fiziki və hüquqi şəxslərə şəffaf, sürətli və əlçatan kredit həlləri təqdim edirik —
                  hər addımda peşəkar yanaşma və sözünün arxasında dayanan dəstəklə.
                </p>
              </div>
            </div>

            {/* Sağ sütun - dəyərlər siyahısı */}
            <div className="lg:col-span-7">
              <div className="divide-y divide-gray-200">
                {[
                  {
                    icon: ShieldCheck,
                    title: 'Etibarlılıq',
                    desc: 'Verdiyimiz sözün arxasında dayanırıq və müştərilərimizlə uzunmüddətli əlaqələr qururuq.'
                  },
                  {
                    icon: Eye,
                    title: 'Şəffaflıq',
                    desc: 'Bütün şərtlər, faizlər və komissiyalar əvvəlcədən açıq şəkildə təqdim olunur.'
                  },
                  {
                    icon: Zap,
                    title: 'Operativlik',
                    desc: 'Müraciətlərə tez yanaşırıq və sənədləşməni növbəsiz, sürətlə tamamlayırıq.'
                  },
                  {
                    icon: UsersRound,
                    title: 'Fərdi yanaşma',
                    desc: 'Hər müştərinin ehtiyacını fərdi öyrənir və ona uyğun kredit həlli təklif edirik.'
                  }
                ].map((v) => {
                  const Icon = v.icon;
                  return (
                    <div
                      key={v.title}
                      className="flex items-start gap-6 py-7 first:pt-0 last:pb-0"
                    >
                      <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-blue-50 flex-shrink-0 mt-1">
                        <Icon className="h-5 w-5 text-blue-700" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{v.title}</h3>
                        <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{v.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};
